import React, { useEffect, useMemo, useState } from "react";
import { Pagination } from "antd";
import { format, parseISO, differenceInDays, isValid } from "date-fns";
import {
  AlertTriangle,
  ChevronDown,
  RefreshCw,
  Search,
  UserPlus,
} from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layout/index";
import kavachShieldIcon from "../../assets/kavachiconnew2.svg";

const PATIENTS_API_URL = process.env.REACT_APP_API_URL_PATIENTS || "";

const parseApiDate = (value) => {
  if (!value) return null;
  const normalized = String(value).includes("T") ? value : String(value).replace(" ", "T");
  const parsed = parseISO(normalized);
  return isValid(parsed) ? parsed : null;
};

const mapMembershipRecord = (row) => {
  const typeLabel = row.membershipProductType || "Kavach";
  const typeVariant = String(typeLabel).toLowerCase().includes("plus") ? "plus" : "kavach";
  const fromDate = parseApiDate(row.fromDate);
  const toDate = parseApiDate(row.toDate);
  let expiringDays = null;
  if (toDate) {
    const daysLeft = differenceInDays(toDate, new Date());
    if (daysLeft >= 0 && daysLeft <= 30) expiringDays = daysLeft;
  }
  const isActive = row.status === 1 || String(row.status).toLowerCase() === "active";
  const name = [row.firstName, row.lastName].filter(Boolean).join(" ").trim() || "—";

  return {
    id: row.membershipCard || row.patientCode || `${row.mobile}-${row.fromDate}`,
    name,
    email: row.email || "—",
    mobile: row.mobile || "—",
    cardNo: row.membershipCard || "—",
    type: typeLabel,
    typeVariant,
    validFrom: fromDate ? format(fromDate, "dd MMM yyyy") : row.fromDate || "—",
    validTo: toDate ? format(toDate, "dd MMM yyyy") : row.toDate || "—",
    amount: Number(row.totalAmount) || 0,
    status: isActive ? "Active" : "Inactive",
    expiringDays,
    patientCode: row.patientCode,
  };
};

const getNewEnrollmentUrl = () => {
  const base = (process.env.REACT_APP_API_URL_PATIENTS || "").replace(/\/dhp\/?$/, "");
  const hospitalId = localStorage.getItem("hospitalid") || "";
  const hospitaluserId = localStorage.getItem("HospitaluserId") || "";
  const params = new URLSearchParams({ from: "hospital", code: hospitaluserId });
  return `${base}/services/membership?${params.toString()}`;
};

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-red-50 text-red-600 border-red-200",
};

const TYPE_STYLES = {
  kavach: "bg-emerald-50 text-emerald-700 border-emerald-200",
  plus: "bg-sky-50 text-sky-700 border-sky-200",
};

const TYPE_DOT = {
  kavach: "bg-emerald-500",
  plus: "bg-sky-500",
};

const StatCard = ({ label, value, valueClass, dotClass }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4">
    <p className="text-[13px] text-slate-500 mb-1">{label}</p>
    <div className="flex items-center gap-2">
      {dotClass && <span className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`} />}
      <p className={`text-2xl font-bold ${valueClass || "text-[#0B4D76]"}`}>{value}</p>
    </div>
  </div>
);

const KavachEnrollment = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem("auth_token");
  const tenantId = localStorage.getItem("tenantId") || "";
  const hospitalCode = localStorage.getItem("hospitalid") || "";
  const hospitaluserId = localStorage.getItem("HospitaluserId") || "";

  const fetchMemberships = async () => {
    if (!hospitaluserId) {
      setMembers([]);
      setTotalCount(0);
      setLoading(false);
      toast.error("Hospital code not found. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      const url = `${PATIENTS_API_URL}Patient_Membership/hospital-memberships?hospitalCode=${encodeURIComponent(hospitaluserId)}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tenantId,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch Kavach memberships");
      }

      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data.map(mapMembershipRecord) : [];
      setMembers(list);
      setTotalCount(json.totalCount ?? list.length);
    } catch (error) {
      console.error("Kavach memberships fetch error:", error);
      toast.error(error?.message || "Failed to load Kavach memberships");
      setMembers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const active = members.filter((m) => m.status === "Active").length;
    const inactive = members.filter((m) => m.status === "Inactive").length;
    const expiring = members.filter((m) => m.expiringDays != null).length;
    return {
      total: totalCount || members.length,
      active,
      inactive,
      expiring,
    };
  }, [members, totalCount]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      const matchSearch =
        !q ||
        m.name.toLowerCase().includes(q) ||
        m.mobile.includes(q) ||
        m.cardNo.toLowerCase().includes(q);
      const matchStatus = statusFilter === "all" || m.status.toLowerCase() === statusFilter;
      const matchType =
        typeFilter === "all" ||
        (typeFilter === "kavach" && m.typeVariant === "kavach") ||
        (typeFilter === "plus" && m.typeVariant === "plus");
      return matchSearch && matchStatus && matchType;
    });
  }, [members, search, statusFilter, typeFilter]);

  const paginated = useMemo(() => {
    const start = pageNumber * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, pageNumber, pageSize]);

  const handleNewEnrollment = () => {
    const url = getNewEnrollmentUrl();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
    setPageNumber(0);
  };

  return (
    <DashboardLayout>
      <div className="mt-6 px-2 sm:px-0 max-w-[1400px]">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
              <img src={kavachShieldIcon} alt="" className="w-6 h-6 object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0B4D76]">Kavach Memberships</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                View and manage all Kavach health protection memberships
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleNewEnrollment}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#004376] to-[#00BA7E] hover:opacity-90 shadow-sm transition-opacity shrink-0"
          >
            <UserPlus size={16} />
            New Kavach Enrollment
          </button>
        </div>

        {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <StatCard label="Total Members" value={stats.total} />
          <StatCard label="Active" value={stats.active} valueClass="text-emerald-600" dotClass="bg-emerald-500" />
          <StatCard label="Inactive" value={stats.inactive} valueClass="text-red-500" dotClass="bg-red-500" />
          <StatCard label="Expiring Soon" value={stats.expiring} valueClass="text-amber-500" dotClass="bg-amber-400" />
        </div> */}
{/* 
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
            <div>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPageNumber(0);
                  }}
                  placeholder="Search by name, mobile or card no."
                  className="w-full h-11 pl-9 pr-3 border border-slate-200 rounded-xl bg-slate-50/50 text-sm outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15"
                />
              </div>
            </div>
            <div className="relative min-w-[140px]">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPageNumber(0);
                }}
                className="w-full h-11 appearance-none pl-3 pr-9 border border-slate-200 rounded-xl bg-slate-50/50 text-sm text-slate-700 outline-none focus:bg-white focus:border-emerald-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <div className="relative min-w-[140px]">
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPageNumber(0);
                }}
                className="w-full h-11 appearance-none pl-3 pr-9 border border-slate-200 rounded-xl bg-slate-50/50 text-sm text-slate-700 outline-none focus:bg-white focus:border-emerald-500"
              >
                <option value="all">All Types</option>
                <option value="kavach">Kavach</option>
                <option value="plus">Kavach Plus</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="h-11 px-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <RefreshCw size={15} />
              Reset
            </button>
          </div>
        </div> */}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#EDF3F8] border-b border-[#DDE8F2]">
                  {[
                    "Member",
                    "Mobile Number",
                    "Membership Id",
                    "Type",
                    "Valid From – To",
                    "Amount",
                    "Status",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3.5 text-left text-[11px] font-bold text-[#0B4D76] uppercase tracking-wide whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-[#004276]">
                      Loading memberships…
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-slate-500">
                      No memberships found
                    </td>
                  </tr>
                ) : (
                  paginated.map((member, index) => (
                    <tr
                      key={member.id}
                      className={`border-b border-[#EEEEEE] hover:bg-[#F0F7FF] transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"
                      }`}
                    >
                      <td className="px-4 py-4">
                        <p className="text-sm font-semibold text-slate-800">{member.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{member.email}</p>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">{member.mobile}</td>
                      <td className="px-4 py-4">
                        <span className="inline-block px-2.5 py-1 rounded-md bg-slate-100 text-xs font-medium text-slate-700">
                          {member.cardNo}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                            TYPE_STYLES[member.typeVariant]
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${TYPE_DOT[member.typeVariant]}`} />
                          {member.type}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-slate-700">
                          {member.validFrom} – {member.validTo}
                        </p>
                        {member.expiringDays != null && member.expiringDays <= 30 && (
                          <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] font-medium text-amber-800">
                            <AlertTriangle size={11} />
                            Expiring in {member.expiringDays} days
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-[#0B4D76]">
                        ₹{member.amount.toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
                            STATUS_STYLES[member.status]
                          }`}
                        >
                          {member.status}
                        </span>
                      </td>
                    
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-slate-500">
              Showing {filtered.length === 0 ? 0 : pageNumber * pageSize + 1}–
              {Math.min((pageNumber + 1) * pageSize, filtered.length)} of{" "}
              {stats.total} members
            </p>
            <Pagination
              current={pageNumber + 1}
              pageSize={pageSize}
              total={filtered.length}
              showSizeChanger
              pageSizeOptions={[6, 10, 20, 50]}
              onChange={(page, size) => {
                setPageNumber((page || 1) - 1);
                if (size !== pageSize) setPageSize(size || 10);
              }}
              onShowSizeChange={(_, size) => {
                setPageSize(size || 10);
                setPageNumber(0);
              }}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default KavachEnrollment;
